import filter from "lodash/filter";
import find from "lodash/find";
import map from "lodash/map";
import sortBy from "lodash/sortBy";
import { create } from "zustand";

import { PanelItem, PanelKey, PanelsActions, PanelsData } from "./side_panel_store.types";

const MAX_OPEN_PANELS_COUNT = 3;

const panelsHighestIndex = (panels: PanelItem[]) => sortBy(panels, "index").pop()?.index ?? 0;

export const useSidePanelsStore = create<PanelsData & { actions: PanelsActions }>()((set, get) => ({
  panels: [],
  actions: {
    openPanel: (
      key: PanelKey,
      data: PanelItem["data"],
      stack = false,
      persist = false,
      disableEntranceAnimation = false
    ) => {
      if (stack) {
        const isOpen = !!find(get().panels, { key });
        if (isOpen) {
          get().actions.setPanelData(key, data);
          return;
        }
        set((state) => ({
          panels: [
            ...state.panels.slice(-MAX_OPEN_PANELS_COUNT + 1),
            {
              key,
              data,
              index: panelsHighestIndex(state.panels) + 1,
              isPersistent: persist,
              disableEntranceAnimation
            }
          ]
        }));
      } else {
        const persistedPanel = filter(
          get().panels,
          (panel) => panel.isPersistent === true && panel.key !== key
        );
        set((state) => ({
          panels: [
            ...persistedPanel,
            {
              key,
              data,
              index: panelsHighestIndex(state.panels) + 1,
              isPersistent: persist,
              disableEntranceAnimation
            }
          ]
        }));
      }
    },
    closePanel: (keyOrSelector: PanelKey | ((panels: PanelItem[]) => PanelItem[])) => {
      if (typeof keyOrSelector === "function") {
        const selectedPanelKeys = map(keyOrSelector(get().panels), "key");
        set((state) => ({ panels: state.panels.filter((item) => !selectedPanelKeys.includes(item.key)) }));
      } else {
        set((state) => ({ panels: state.panels.filter((item) => item.key !== keyOrSelector) }));
      }
    },
    setPanelIsClosing: (key: PanelKey, isClosing: boolean) =>
      set((state) => ({
        panels: state.panels.map((item) => (item.key === key ? { ...item, isClosing } : item))
      })),
    setPanelData: (
      key: PanelKey,
      newData: PanelItem["data"] | ((data: PanelItem["data"]) => PanelItem["data"]),
      setOnTop = true
    ) =>
      set((state) => ({
        panels: state.panels.map((item) =>
          item.key === key
            ? {
                ...item,
                data: typeof newData === "function" ? newData(item.data) : newData,
                index: setOnTop ? panelsHighestIndex(state.panels) + 1 : item.index
              }
            : item
        )
      })),
    updatePanelsData: (type, newData) =>
      set((state) => ({
        panels: state.panels.map((item) =>
          item.key.startsWith(type)
            ? {
                ...item,
                data: {
                  ...item.data,
                  ...(typeof newData === "function" ? newData(item.data) : newData)
                }
              }
            : item
        )
      })),
    closeAllPanels: () => set({ panels: [] })
  }
}));
